import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconAffiliate, IconMessage, IconReportSearch, IconUserQuestion, IconX } from '@tabler/icons-react';
import { Ticket } from 'lucide-react';
import { useBoardChatRealtime } from '../context/BoardChatRealtimeProvider';
import { formatBadge } from '../utils/boardChatUtils';
import {
  BOARD_CHAT_LAYOUTS,
  BOARD_CHAT_OPEN_EVENT,
  BOARD_CHAT_OPEN_SIDEBAR_EVENT,
  BOARD_CHAT_ROUTE,
  consumeBoardChatOpenLayout,
  consumeBoardChatSidebarOpenFlag,
  getBoardChatLayout,
  setBoardChatLayout,
} from '../utils/boardChatLayout';
import {
  BOARD_CADASTRO_LAYOUTS,
  BOARD_CADASTRO_ROUTE,
  getBoardCadastroLayout,
  setBoardCadastroLayout,
} from '../utils/boardCadastroLayout';
import { AUTH_SESSION_CHANGE_EVENT } from '../../../utils/authSession';
import { BOARD_DATA_REFRESH_EVENT } from '../../../utils/boardRefreshEvents';
import {
  countOpenOrdersByStatus,
  fetchOsStatusInicial,
  invalidateOsStatusInicialCache,
  listOpenOrdersByStatus,
  resolveOpenStatusNome,
} from '../services/boardNovosChamadosService';
import CustomersCadastroUsuariosView from '../../users-customers/components/CustomersCadastroUsuariosView';
import { BoardChatLayoutSwitcher } from './BoardChatLayoutSwitcher';
import { BoardChatPanel } from './BoardChatPanel';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import './BoardChatPanel.css';

const CADASTRO_TITLE = 'Customers - solicitação de cadastro pendente';
const NOVOS_CHAMADOS_SUB = 'OS Em aberto no status de abertura do portal';

function isTypingTarget(target) {
  if (!target || !target.tagName) return false;
  const t = target.tagName.toLowerCase();
  if (t === 'input' || t === 'textarea' || t === 'select') return true;
  if (target.isContentEditable) return true;
  return false;
}

function CustomersModuleLabel({ visible, children }) {
  return (
    <span
      className={`bch-customers-module-label-wrap${visible ? ' is-visible' : ''}`}
      aria-hidden={!visible}
    >
      <span className="bch-customers-module-label-clip">
        <span className="bch-customers-module-label">{children}</span>
      </span>
    </span>
  );
}

function ChatShortcutTooltip({ open, anchorRef }) {
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef.current) {
      setCoords(null);
      return undefined;
    }
    const update = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      setCoords({ left: rect.left, top: rect.bottom + 8 });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef]);

  if (!open || !coords) return null;

  return createPortal(
    <div
      className="bch-chat-shortcut-tooltip"
      style={{ left: coords.left, top: coords.top }}
      role="tooltip"
      aria-hidden="true"
    >
      <span className="bch-chat-shortcut-tooltip-label">Atalho:</span>
      <span className="bch-chat-shortcut-kbds" aria-hidden="true">
        <kbd className="bch-chat-shortcut-kbd">Ctrl</kbd>
        <span className="bch-chat-shortcut-plus">+</span>
        <kbd className="bch-chat-shortcut-kbd">H</kbd>
      </span>
    </div>,
    document.body
  );
}

function NovosChamadosTooltip({ open, anchorRef, rows }) {
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef?.current || !rows?.length) {
      setCoords(null);
      return undefined;
    }
    const update = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const pad = 8;
      const estimatedWidth = 280;
      let left = rect.left + rect.width / 2;
      left = Math.min(Math.max(left, pad + estimatedWidth / 2), window.innerWidth - pad - estimatedWidth / 2);
      setCoords({ left, top: rect.bottom + 10 });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef, rows]);

  if (!open || !coords || !rows?.length) return null;

  return createPortal(
    <div
      className="bch-novos-chamados-tooltip"
      style={{ left: coords.left, top: coords.top }}
      role="tooltip"
      aria-hidden="true"
    >
      <ul className="bch-novos-chamados-tooltip-list">
        {rows.map((row) => (
          <li key={row.id} className="bch-novos-chamados-tooltip-row">
            <span className="bch-novos-chamados-tooltip-os">{row.id}</span>
            <span className="bch-novos-chamados-tooltip-cliente" title={row.cliente}>
              {row.cliente}
            </span>
          </li>
        ))}
      </ul>
    </div>,
    document.body
  );
}

async function fetchPendingCadastroCount() {
  try {
    const token = localStorage.getItem('authToken');
    // Mesmo endpoint da listagem (confiável com a sessão); /count como fallback.
    const res = await fetch('/api/vc-usr/users-pending', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success !== false) {
      if (json.count != null) return Number(json.count) || 0;
      if (Array.isArray(json.data)) return json.data.length;
    }
    const resCount = await fetch('/api/vc-usr/users-pending/count', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const jsonCount = await resCount.json().catch(() => ({}));
    if (!resCount.ok || jsonCount.success === false) return null;
    return Number(jsonCount.data?.count) || 0;
  } catch {
    return null;
  }
}

/**
 * Menu Customers na toolbar do Board.
 * Abre overlay sobre o Board e sincroniza a URL /app/chat ou /app/customers-notificacao-cadastro.
 */
export function BoardChatToolbarButton({
  onOpenOrderDetails,
  onEncaminharOs,
  chatOnly = false,
  cadastroOnly = false,
  canAccessCadastro = false,
  underHeader = false,
  onPendingCountChange,
  openOrderGroups = null,
  onFilterNovosChamados = null,
  emAbertoStatusFilter = null,
  onMenuOpenChange = null,
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);
  const chatBtnRef = useRef(null);
  const preAtendBtnRef = useRef(null);
  const novosChamadosBtnRef = useRef(null);
  const realtime = useBoardChatRealtime();
  const realtimeStatus = realtime?.status || 'idle';
  const hasAccess = Boolean(realtime?.hasAccess);
  const session = realtime?.session || null;
  const unreadCount = Number(realtime?.unreadCount) || 0;
  const inboxCount = Number(realtime?.inboxCount) || 0;
  const preAtendimentoUnreadCount = Number(realtime?.preAtendimentoUnreadCount) || 0;
  const preAtendimentoInboxCount = Number(realtime?.preAtendimentoInboxCount) || 0;
  const preAtendimentoPendingOsCount = Number(realtime?.preAtendimentoPendingOsCount) || 0;
  const preAtendimentoTeamId = realtime?.preAtendimentoTeamId ?? null;
  const refreshBadges = realtime?.refreshBadges || (() => {});

  const [panelOpen, setPanelOpen] = useState(
    () => chatOnly || location.pathname === BOARD_CHAT_ROUTE
  );
  const [targetTeamId, setTargetTeamId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [shortcutTipOpen, setShortcutTipOpen] = useState(false);
  const [novosChamadosTipOpen, setNovosChamadosTipOpen] = useState(false);
  const [layout, setLayout] = useState(() =>
    chatOnly ? BOARD_CHAT_LAYOUTS.fullscreen : getBoardChatLayout()
  );
  const [unreadBadgeAnim, setUnreadBadgeAnim] = useState(false);
  const prevUnreadRef = useRef(unreadCount);

  const [preUnreadBadgeAnim, setPreUnreadBadgeAnim] = useState(false);
  const prevPreUnreadRef = useRef(preAtendimentoUnreadCount);

  const [pendingCount, setPendingCount] = useState(0);
  const [pendingBadgeAnim, setPendingBadgeAnim] = useState(false);
  const prevPendingRef = useRef(0);
  const [osStatusInicial, setOsStatusInicial] = useState('');
  const [novosChamadosBadgeAnim, setNovosChamadosBadgeAnim] = useState(false);
  const prevNovosChamadosRef = useRef(0);
  const [cadastroOpen, setCadastroOpen] = useState(
    () =>
      Boolean(cadastroOnly) ||
      (Boolean(canAccessCadastro) && location.pathname === BOARD_CADASTRO_ROUTE)
  );
  const [cadastroLayout, setCadastroLayout] = useState(() =>
    cadastroOnly ? BOARD_CADASTRO_LAYOUTS.fullscreen : getBoardCadastroLayout()
  );

  const showNovosChamados = !chatOnly && !cadastroOnly && Array.isArray(openOrderGroups);

  const novosChamadosCount = useMemo(
    () => (showNovosChamados ? countOpenOrdersByStatus(openOrderGroups, osStatusInicial) : 0),
    [showNovosChamados, openOrderGroups, osStatusInicial]
  );

  const novosChamadosRows = useMemo(
    () => (showNovosChamados ? listOpenOrdersByStatus(openOrderGroups, osStatusInicial) : []),
    [showNovosChamados, openOrderGroups, osStatusInicial]
  );

  const novosChamadosStatusNome = useMemo(
    () => (showNovosChamados ? resolveOpenStatusNome(openOrderGroups, osStatusInicial) : ''),
    [showNovosChamados, openOrderGroups, osStatusInicial]
  );

  const novosChamadosActive = useMemo(() => {
    if (!novosChamadosStatusNome || !Array.isArray(emAbertoStatusFilter)) return false;
    return (
      emAbertoStatusFilter.length === 1 &&
      String(emAbertoStatusFilter[0] || '').trim() === novosChamadosStatusNome
    );
  }, [novosChamadosStatusNome, emAbertoStatusFilter]);

  const refreshOsStatusInicial = useCallback(async (force = false) => {
    if (!showNovosChamados) return;
    const next = await fetchOsStatusInicial({ force });
    if (next != null) setOsStatusInicial(next);
  }, [showNovosChamados]);

  const refreshPendingCount = useCallback(async () => {
    const next = await fetchPendingCadastroCount();
    // null = falha — mantém o valor anterior (evita sumir o badge)
    if (next == null) return;
    setPendingCount(next);
    return next;
  }, []);

  useEffect(() => {
    if (typeof onPendingCountChange === 'function') {
      onPendingCountChange(pendingCount);
    }
  }, [pendingCount, onPendingCountChange]);

  useEffect(() => {
    if (!showNovosChamados) {
      setOsStatusInicial('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const next = await fetchOsStatusInicial();
      if (!cancelled && next != null) setOsStatusInicial(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [showNovosChamados]);

  useEffect(() => {
    if (!canAccessCadastro) {
      setPendingCount(0);
      return undefined;
    }
    let cancelled = false;
    const run = async () => {
      const next = await fetchPendingCadastroCount();
      if (!cancelled && next != null) setPendingCount(next);
    };
    run();
    const t1 = setTimeout(run, 1200);
    const t2 = setTimeout(run, 3500);
    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [canAccessCadastro]);

  useEffect(() => {
    if (!canAccessCadastro || !hasAccess) return undefined;
    let cancelled = false;
    (async () => {
      const next = await fetchPendingCadastroCount();
      if (!cancelled && next != null) setPendingCount(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [hasAccess, canAccessCadastro]);

  useEffect(() => {
    const onAuth = (e) => {
      if (e?.detail?.active) {
        if (canAccessCadastro) {
          fetchPendingCadastroCount().then((next) => {
            if (next != null) setPendingCount(next);
          });
        } else {
          setPendingCount(0);
        }
        if (showNovosChamados) {
          invalidateOsStatusInicialCache();
          refreshOsStatusInicial(true);
        }
      } else {
        setPendingCount(0);
        setOsStatusInicial('');
      }
    };
    const onBoardRefresh = () => {
      if (canAccessCadastro) {
        fetchPendingCadastroCount().then((next) => {
          if (next != null) setPendingCount(next);
        });
      }
      if (showNovosChamados) {
        refreshOsStatusInicial(false);
      }
    };
    window.addEventListener(AUTH_SESSION_CHANGE_EVENT, onAuth);
    window.addEventListener(BOARD_DATA_REFRESH_EVENT, onBoardRefresh);
    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGE_EVENT, onAuth);
      window.removeEventListener(BOARD_DATA_REFRESH_EVENT, onBoardRefresh);
    };
  }, [canAccessCadastro, showNovosChamados, refreshOsStatusInicial]);

  useEffect(() => {
    const prev = Number(prevNovosChamadosRef.current) || 0;
    const next = Number(novosChamadosCount) || 0;
    prevNovosChamadosRef.current = next;
    if (next <= prev) return undefined;
    setNovosChamadosBadgeAnim(true);
    const t = setTimeout(() => setNovosChamadosBadgeAnim(false), 1000);
    return () => clearTimeout(t);
  }, [novosChamadosCount]);

  const handleNovosChamados = useCallback(() => {
    setMenuOpen(false);
    setNovosChamadosTipOpen(false);
    if (typeof onFilterNovosChamados !== 'function') return;
    if (novosChamadosActive) {
      onFilterNovosChamados(null);
      return;
    }
    if (!novosChamadosStatusNome) return;
    onFilterNovosChamados(novosChamadosStatusNome);
  }, [onFilterNovosChamados, novosChamadosActive, novosChamadosStatusNome]);

  useEffect(() => {
    if (cadastroOnly) {
      const forced = setBoardCadastroLayout(BOARD_CADASTRO_LAYOUTS.fullscreen);
      setCadastroLayout(forced);
      setCadastroOpen(true);
      setPanelOpen(false);
      if (location.pathname !== BOARD_CADASTRO_ROUTE) {
        navigate(BOARD_CADASTRO_ROUTE, { replace: true });
      }
      return;
    }
    if (!canAccessCadastro) {
      setCadastroOpen(false);
      if (location.pathname === BOARD_CADASTRO_ROUTE) {
        navigate('/app', { replace: true });
      }
      return;
    }
    if (location.pathname === BOARD_CADASTRO_ROUTE) {
      setCadastroLayout(getBoardCadastroLayout());
      setCadastroOpen(true);
      return;
    }
    if (cadastroOpen && location.pathname !== BOARD_CADASTRO_ROUTE) {
      setCadastroOpen(false);
    }
  }, [canAccessCadastro, cadastroOnly, location.pathname, cadastroOpen, navigate]);

  useEffect(() => {
    const prev = Number(prevPendingRef.current) || 0;
    const next = Number(pendingCount) || 0;
    prevPendingRef.current = next;
    if (next <= prev) return undefined;
    setPendingBadgeAnim(true);
    const t = setTimeout(() => setPendingBadgeAnim(false), 1000);
    return () => clearTimeout(t);
  }, [pendingCount]);

  const openPanel = (nextLayout) => {
    const requestedLayout = chatOnly
      ? BOARD_CHAT_LAYOUTS.fullscreen
      : nextLayout || getBoardChatLayout();
    const saved = setBoardChatLayout(requestedLayout);
    setLayout(saved);
    setPanelOpen(true);
    if (location.pathname !== BOARD_CHAT_ROUTE) {
      navigate(BOARD_CHAT_ROUTE);
    }
  };

  const closePanel = () => {
    if (chatOnly) return;
    setPanelOpen(false);
    setTargetTeamId(null);
    refreshBadges();
    if (location.pathname === BOARD_CHAT_ROUTE) {
      navigate('/app');
    }
  };

  const openCadastro = () => {
    if (!canAccessCadastro) return;
    setMenuOpen(false);
    setPanelOpen(false);
    setCadastroLayout(getBoardCadastroLayout());
    setCadastroOpen(true);
    if (location.pathname !== BOARD_CADASTRO_ROUTE) {
      navigate(BOARD_CADASTRO_ROUTE);
    }
  };

  const closeCadastro = () => {
    if (cadastroOnly) return;
    setCadastroOpen(false);
    refreshPendingCount();
    if (location.pathname === BOARD_CADASTRO_ROUTE) {
      navigate('/app');
    }
  };

  const handleCadastroLayoutChange = (next) => {
    if (cadastroOnly) return;
    const saved = setBoardCadastroLayout(next);
    setCadastroLayout(saved);
    setCadastroOpen(true);
    if (location.pathname !== BOARD_CADASTRO_ROUTE) {
      navigate(BOARD_CADASTRO_ROUTE);
    }
  };

  const cadastroLayoutClass =
    cadastroOnly || cadastroLayout === BOARD_CADASTRO_LAYOUTS.fullscreen
      ? 'bch-layout-fullscreen'
      : cadastroLayout === BOARD_CADASTRO_LAYOUTS.sidebar
        ? 'bch-layout-sidebar'
        : 'bch-layout-modal';

  const underHeaderClass = underHeader ? ' bch-overlay--under-header' : '';

  const renderCadastroOverlay = () => {
    if ((!canAccessCadastro && !cadastroOnly) || !cadastroOpen) return null;
    const layoutLocked = Boolean(cadastroOnly);
    return createPortal(
      <div
        className={`bch-overlay ${cadastroLayoutClass} bch-cadastro-overlay${underHeaderClass}`}
        role="dialog"
        aria-modal="true"
        aria-label={CADASTRO_TITLE}
      >
        <div className="bch-overlay-backdrop" aria-hidden="true" />
        <div className={`bch-panel ${cadastroLayoutClass} bch-cadastro-panel`}>
          <div className="bch-cadastro-panel-head">
            <div className="bch-cadastro-panel-title">
              <IconUserQuestion size={16} stroke={1.8} aria-hidden />
              <span>{CADASTRO_TITLE}</span>
            </div>
            <div className="bch-cadastro-panel-actions">
              {layoutLocked ? null : (
                <BoardChatLayoutSwitcher
                  layout={cadastroLayout}
                  onChange={handleCadastroLayoutChange}
                />
              )}
              {layoutLocked ? null : (
                <button
                  type="button"
                  className="bch-cadastro-panel-close"
                  onClick={closeCadastro}
                  aria-label="Fechar"
                >
                  <IconX size={16} stroke={1.8} />
                </button>
              )}
            </div>
          </div>
          <div className="bch-cadastro-panel-body">
            <CustomersCadastroUsuariosView
              onPendingChange={setPendingCount}
              showRefresh={Boolean(cadastroOnly)}
            />
          </div>
        </div>
      </div>,
      document.body
    );
  };

  useEffect(() => {
    const onSidebarEvent = () => openPanel(BOARD_CHAT_LAYOUTS.sidebar);
    const onOpenEvent = (e) => openPanel(e?.detail?.layout || getBoardChatLayout());
    window.addEventListener(BOARD_CHAT_OPEN_SIDEBAR_EVENT, onSidebarEvent);
    window.addEventListener(BOARD_CHAT_OPEN_EVENT, onOpenEvent);
    if (consumeBoardChatSidebarOpenFlag()) openPanel(BOARD_CHAT_LAYOUTS.sidebar);
    const pending = consumeBoardChatOpenLayout();
    if (pending) openPanel(pending);
    return () => {
      window.removeEventListener(BOARD_CHAT_OPEN_SIDEBAR_EVENT, onSidebarEvent);
      window.removeEventListener(BOARD_CHAT_OPEN_EVENT, onOpenEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot listeners once
  }, []);

  useEffect(() => {
    if (chatOnly) {
      const forcedLayout = setBoardChatLayout(BOARD_CHAT_LAYOUTS.fullscreen);
      setLayout(forcedLayout);
      setPanelOpen(true);
      if (location.pathname !== BOARD_CHAT_ROUTE) {
        navigate(BOARD_CHAT_ROUTE, { replace: true });
      }
      return;
    }
    if (location.pathname === BOARD_CHAT_ROUTE) {
      setLayout(getBoardChatLayout());
      setPanelOpen(true);
      return;
    }
    if (panelOpen && location.pathname !== BOARD_CHAT_ROUTE) {
      setPanelOpen(false);
    }
  }, [chatOnly, location.pathname, navigate]);

  const toggleCustomersMenu = useCallback(() => {
    setMenuOpen((v) => {
      const next = !v;
      if (next && typeof onMenuOpenChange === 'function') onMenuOpenChange(true);
      return next;
    });
  }, [onMenuOpenChange]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    // capture: outros botões da toolbar (ex. Menu) fazem stopPropagation no mousedown
    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    const prev = Number(prevUnreadRef.current) || 0;
    const next = Number(unreadCount) || 0;
    prevUnreadRef.current = next;
    if (next <= prev) return undefined;
    setUnreadBadgeAnim(true);
    const t = setTimeout(() => setUnreadBadgeAnim(false), 1000);
    return () => clearTimeout(t);
  }, [unreadCount]);

  useEffect(() => {
    const prev = Number(prevPreUnreadRef.current) || 0;
    const next = Number(preAtendimentoUnreadCount) || 0;
    prevPreUnreadRef.current = next;
    if (next <= prev) return undefined;
    setPreUnreadBadgeAnim(true);
    const t = setTimeout(() => setPreUnreadBadgeAnim(false), 1000);
    return () => clearTimeout(t);
  }, [preAtendimentoUnreadCount]);

  useEffect(() => {
    if (!hasAccess) return undefined;
    const onKey = (e) => {
      if (!e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;
      const k = typeof e.key === 'string' ? e.key.toLowerCase() : '';
      if (k !== 'h') return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      setMenuOpen(false);
      setShortcutTipOpen(false);
      setTargetTeamId(null);
      openPanel(getBoardChatLayout());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- atalho global Ctrl+H
  }, [hasAccess]);

  const handleOpenChat = () => {
    setMenuOpen(false);
    setShortcutTipOpen(false);
    setTargetTeamId(null);
    openPanel(getBoardChatLayout());
  };

  const handleOpenPreAtendimento = () => {
    setMenuOpen(false);
    setShortcutTipOpen(false);
    if (preAtendimentoTeamId != null) {
      setTargetTeamId(preAtendimentoTeamId);
    }
    openPanel(getBoardChatLayout());
  };

  const handleLayoutChange = (next) => {
    const saved = setBoardChatLayout(chatOnly ? BOARD_CHAT_LAYOUTS.fullscreen : next);
    setLayout(saved);
    setPanelOpen(true);
    if (location.pathname !== BOARD_CHAT_ROUTE) {
      navigate(BOARD_CHAT_ROUTE);
    }
  };

  if (cadastroOnly) {
    return renderCadastroOverlay();
  }

  if (!hasAccess && chatOnly) {
    return (
      <div className="bch-chat-only-status">
        {realtimeStatus === 'loading' || realtimeStatus === 'idle'
          ? 'Carregando chat…'
          : 'Chat indisponível para este usuário.'}
      </div>
    );
  }

  // Sem acesso ao chat e sem modo chatOnly: exibe só Cadastro se permitido.
  if (!hasAccess && !chatOnly) {
    if (!canAccessCadastro && !showNovosChamados) return null;
    const pendingLabelOnly = formatBadge(pendingCount);
    const novosChamadosLabelOnly = formatBadge(novosChamadosCount);
    return (
      <>
        <div className="bch-toolbar-wrap" ref={rootRef}>
          <div className="board-clickup-icon-btn-anchor bch-customers-anchor">
            <div
              className={[
                'board-clickup-filter-chip',
                'bch-customers-trigger',
                menuOpen || cadastroOpen ? 'is-open' : '',
                cadastroOpen ? 'has-selection' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <button
                type="button"
                className="bch-customers-brand-btn"
                aria-label="Menu de ações Customers"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                onClick={toggleCustomersMenu}
              >
                <span
                  className="board-clickup-filter-chip-icon bch-customers-brand-icon"
                  aria-hidden="true"
                  data-tooltip="Menu de ações Customers"
                >
                  <IconAffiliate size={14} stroke={1.75} />
                </span>
              </button>
              {canAccessCadastro ? (
                <>
                  <span className="bch-customers-sep" aria-hidden="true">
                    ·
                  </span>
                  <button
                    type="button"
                    className={`bch-customers-module${pendingLabelOnly ? ' has-badge' : ''}`}
                    aria-label="Abrir Cadastro de usuários"
                    onClick={openCadastro}
                  >
                    <CustomersModuleLabel visible={Boolean(pendingLabelOnly)}>Cadastro</CustomersModuleLabel>
                    <span className="bch-customers-chat-icon" aria-hidden="true">
                      <IconUserQuestion size={13} stroke={1.8} />
                    </span>
                    {pendingLabelOnly ? (
                      <>
                        <span className="bch-customers-badge-sep" aria-hidden="true">
                          ·
                        </span>
                        <span className="bch-customers-module-badges">
                          <span
                            className={[
                              'bch-badge',
                              'bch-badge--unread',
                              'bch-customers-tip',
                              pendingBadgeAnim ? 'is-animate' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            data-tooltip="Cadastros pendentes"
                          >
                            {pendingLabelOnly}
                          </span>
                        </span>
                      </>
                    ) : null}
                  </button>
                </>
              ) : null}
              {showNovosChamados ? (
                <>
                  <span className="bch-customers-sep" aria-hidden="true">
                    ·
                  </span>
                  <button
                    ref={novosChamadosBtnRef}
                    type="button"
                    className={`bch-customers-module${novosChamadosActive ? ' is-active' : ''}${novosChamadosLabelOnly ? ' has-badge' : ''}`}
                    aria-label="Filtrar novos chamados do portal"
                    aria-pressed={novosChamadosActive}
                    onClick={handleNovosChamados}
                    onMouseEnter={() => setNovosChamadosTipOpen(true)}
                    onMouseLeave={() => setNovosChamadosTipOpen(false)}
                  >
                    <CustomersModuleLabel visible={Boolean(novosChamadosLabelOnly)}>
                      Chamados
                    </CustomersModuleLabel>
                    <span className="bch-customers-chat-icon" aria-hidden="true">
                      <Ticket size={13} strokeWidth={1.75} />
                    </span>
                    {novosChamadosLabelOnly ? (
                      <>
                        <span className="bch-customers-badge-sep" aria-hidden="true">
                          ·
                        </span>
                        <span className="bch-customers-module-badges">
                          <span
                            className={[
                              'bch-badge',
                              'bch-badge--unread',
                              novosChamadosBadgeAnim ? 'is-animate' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            {novosChamadosLabelOnly}
                          </span>
                        </span>
                      </>
                    ) : null}
                  </button>
                  <NovosChamadosTooltip
                    open={novosChamadosTipOpen}
                    anchorRef={novosChamadosBtnRef}
                    rows={novosChamadosRows}
                  />
                </>
              ) : null}
            </div>
            {menuOpen ? (
              <div className="ec-actions-menu bch-customers-menu" role="menu" aria-label="Menu de ações Customers">
                {canAccessCadastro ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={`ec-actions-menu-item${cadastroOpen ? ' is-active' : ''}`}
                    onClick={openCadastro}
                  >
                    <IconUserQuestion size={16} stroke={1.5} aria-hidden />
                    <span className="bch-customers-menu-item-text">
                      <span className="bch-customers-menu-item-title">Cadastro</span>
                      <span className="bch-customers-menu-item-sub">Usuários pendentes do portal</span>
                    </span>
                    {pendingLabelOnly ? (
                      <span className="bch-customers-menu-item-badges" aria-hidden="true">
                        <span className="bch-badge bch-badge--unread">{pendingLabelOnly}</span>
                      </span>
                    ) : null}
                  </button>
                ) : null}
                {showNovosChamados ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={`ec-actions-menu-item${novosChamadosActive ? ' is-active' : ''}`}
                    onClick={handleNovosChamados}
                  >
                    <Ticket size={16} strokeWidth={1.5} aria-hidden />
                    <span className="bch-customers-menu-item-text">
                      <span className="bch-customers-menu-item-title">Novos chamados</span>
                      <span className="bch-customers-menu-item-sub">{NOVOS_CHAMADOS_SUB}</span>
                    </span>
                    {novosChamadosLabelOnly ? (
                      <span className="bch-customers-menu-item-badges" aria-hidden="true">
                        <span className="bch-badge bch-badge--unread">{novosChamadosLabelOnly}</span>
                      </span>
                    ) : null}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <span className="board-toolbar-separator" aria-hidden="true">
          |
        </span>
        {renderCadastroOverlay()}
      </>
    );
  }

  const unreadLabel = formatBadge(unreadCount);
  const inboxLabel = formatBadge(inboxCount);
  const hasChatBadge = Boolean(unreadLabel || inboxLabel);

  const preUnreadLabel = formatBadge(preAtendimentoUnreadCount);
  const preInboxLabel = formatBadge(preAtendimentoInboxCount);
  const prePendingOsLabel = formatBadge(preAtendimentoPendingOsCount);
  const hasPreBadge = Boolean(preUnreadLabel || preInboxLabel || prePendingOsLabel);

  const pendingLabel = formatBadge(pendingCount);
  const novosChamadosLabel = formatBadge(novosChamadosCount);
  const chatActive = panelOpen || location.pathname === BOARD_CHAT_ROUTE;

  if (chatOnly) {
    return (
      <BoardChatPanel
        open
        onClose={() => {}}
        session={session}
        onBadgesRefresh={refreshBadges}
        layout={BOARD_CHAT_LAYOUTS.fullscreen}
        onLayoutChange={() => {}}
        onOpenOrderDetails={onOpenOrderDetails}
        onEncaminharOs={onEncaminharOs}
        layoutLocked
        underHeader={underHeader}
      />
    );
  }

  return (
    <>
      <div className="bch-toolbar-wrap" ref={rootRef}>
        <div className="board-clickup-icon-btn-anchor bch-customers-anchor">
          <div
            className={[
              'board-clickup-filter-chip',
              'bch-customers-trigger',
              menuOpen || chatActive || cadastroOpen ? 'is-open' : '',
              chatActive || cadastroOpen ? 'has-selection' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <button
              type="button"
              className="bch-customers-brand-btn"
              aria-label="Menu de ações Customers"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={toggleCustomersMenu}
            >
              <span
                className="board-clickup-filter-chip-icon bch-customers-brand-icon"
                aria-hidden="true"
                data-tooltip="Menu de ações Customers"
              >
                <IconAffiliate size={14} stroke={1.75} />
              </span>
            </button>
            <span className="bch-customers-sep" aria-hidden="true">
              ·
            </span>
            {/* Módulo Chat normal (exibido quando tem badge de chat OU quando não há badge de pré-atendimento) */}
            {(hasChatBadge || !hasPreBadge) && (
              <button
                ref={chatBtnRef}
                type="button"
                className={`bch-customers-module${hasChatBadge ? ' has-badge' : ''}${chatActive && !targetTeamId ? ' is-active' : ''}`}
                aria-label="Abrir Chat"
                aria-keyshortcuts="Control+H"
                onClick={handleOpenChat}
                onMouseEnter={(e) => {
                  if (!e.target.closest?.('.bch-customers-module-badges')) setShortcutTipOpen(true);
                }}
                onMouseLeave={() => setShortcutTipOpen(false)}
              >
                <CustomersModuleLabel visible={hasChatBadge}>Chat</CustomersModuleLabel>
                <span className="bch-customers-chat-icon" aria-hidden="true">
                  <IconMessage size={13} stroke={1.8} />
                </span>
                {hasChatBadge && (
                  <>
                    <span className="bch-customers-badge-sep" aria-hidden="true">
                      ·
                    </span>
                    <span
                      className="bch-customers-module-badges"
                      onMouseEnter={() => setShortcutTipOpen(false)}
                      onMouseLeave={(e) => {
                        if (chatBtnRef.current?.contains(e.relatedTarget)) setShortcutTipOpen(true);
                      }}
                    >
                      {unreadLabel ? (
                        <span
                          className={[
                            'bch-badge',
                            'bch-badge--unread',
                            'bch-customers-tip',
                            unreadBadgeAnim ? 'is-animate' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          data-tooltip="Mensagens não lidas"
                        >
                          {unreadLabel}
                        </span>
                      ) : null}
                      {inboxLabel ? (
                        <span
                          className="bch-badge bch-badge--inbox bch-customers-tip"
                          data-tooltip="Mensagens em aberto, pendente ou novas"
                        >
                          {inboxLabel}
                        </span>
                      ) : null}
                    </span>
                  </>
                )}
              </button>
            )}

            {/* Módulo Pré-atendimento (exibido de forma diferenciada quando há mensagens novas ou em andamento) */}
            {hasPreBadge && (
              <>
                {hasChatBadge && (
                  <span className="bch-customers-sep" aria-hidden="true">
                    ·
                  </span>
                )}
                <button
                  ref={preAtendBtnRef}
                  type="button"
                  className={`bch-customers-module bch-customers-module--pre-atend has-badge${chatActive && targetTeamId ? ' is-active' : ''}`}
                  aria-label="Abrir Pré-atendimento"
                  onClick={handleOpenPreAtendimento}
                >
                  <CustomersModuleLabel visible>Pré-atendimento</CustomersModuleLabel>
                  <span className="bch-customers-chat-icon" aria-hidden="true">
                    <IconReportSearch size={13} stroke={1.8} />
                  </span>
                  <span className="bch-customers-badge-sep" aria-hidden="true">
                    ·
                  </span>
                  <span className="bch-customers-module-badges">
                    {preUnreadLabel ? (
                      <span
                        className={[
                          'bch-badge',
                          'bch-badge--unread',
                          'bch-customers-tip',
                          preUnreadBadgeAnim ? 'is-animate' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        data-tooltip="Pré-atendimentos novos (não lidos)"
                      >
                        {preUnreadLabel}
                      </span>
                    ) : null}
                    {preInboxLabel ? (
                      <span
                        className="bch-badge bch-badge--inbox bch-customers-tip"
                        data-tooltip="Pré-atendimentos em andamento ou pendentes"
                      >
                        {preInboxLabel}
                      </span>
                    ) : null}
                    {prePendingOsLabel ? (
                      <span
                        className="bch-badge bch-badge--os bch-customers-tip"
                        data-tooltip="OS ainda não encaminhadas ou fechadas no pré-atendimento"
                      >
                        {prePendingOsLabel}
                      </span>
                    ) : null}
                  </span>
                </button>
              </>
            )}
            {canAccessCadastro ? (
              <>
                <span className="bch-customers-sep" aria-hidden="true">
                  ·
                </span>
                <button
                  type="button"
                  className={`bch-customers-module${pendingLabel ? ' has-badge' : ''}`}
                  aria-label="Abrir Cadastro de usuários"
                  onClick={openCadastro}
                >
                  <CustomersModuleLabel visible={Boolean(pendingLabel)}>Cadastro</CustomersModuleLabel>
                  <span className="bch-customers-chat-icon" aria-hidden="true">
                    <IconUserQuestion size={13} stroke={1.8} />
                  </span>
                  {pendingLabel ? (
                    <>
                      <span className="bch-customers-badge-sep" aria-hidden="true">
                        ·
                      </span>
                      <span className="bch-customers-module-badges">
                        <span
                          className={[
                            'bch-badge',
                            'bch-badge--unread',
                            'bch-customers-tip',
                            pendingBadgeAnim ? 'is-animate' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          data-tooltip="Cadastros pendentes"
                        >
                          {pendingLabel}
                        </span>
                      </span>
                    </>
                  ) : null}
                </button>
              </>
            ) : null}
            {showNovosChamados ? (
              <>
                <span className="bch-customers-sep" aria-hidden="true">
                  ·
                </span>
                <button
                  ref={novosChamadosBtnRef}
                  type="button"
                  className={`bch-customers-module${novosChamadosActive ? ' is-active' : ''}${novosChamadosLabel ? ' has-badge' : ''}`}
                  aria-label="Filtrar novos chamados do portal"
                  aria-pressed={novosChamadosActive}
                  onClick={handleNovosChamados}
                  onMouseEnter={() => setNovosChamadosTipOpen(true)}
                  onMouseLeave={() => setNovosChamadosTipOpen(false)}
                >
                  <CustomersModuleLabel visible={Boolean(novosChamadosLabel)}>
                    Chamados
                  </CustomersModuleLabel>
                  <span className="bch-customers-chat-icon" aria-hidden="true">
                    <Ticket size={13} strokeWidth={1.75} />
                  </span>
                  {novosChamadosLabel ? (
                    <>
                      <span className="bch-customers-badge-sep" aria-hidden="true">
                        ·
                      </span>
                      <span className="bch-customers-module-badges">
                        <span
                          className={[
                            'bch-badge',
                            'bch-badge--unread',
                            novosChamadosBadgeAnim ? 'is-animate' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {novosChamadosLabel}
                        </span>
                      </span>
                    </>
                  ) : null}
                </button>
                <NovosChamadosTooltip
                  open={novosChamadosTipOpen}
                  anchorRef={novosChamadosBtnRef}
                  rows={novosChamadosRows}
                />
              </>
            ) : null}
            <ChatShortcutTooltip open={shortcutTipOpen} anchorRef={chatBtnRef} />
          </div>

          {menuOpen ? (
            <div className="ec-actions-menu bch-customers-menu" role="menu" aria-label="Menu de ações Customers">
              <button
                type="button"
                role="menuitem"
                className={`ec-actions-menu-item${chatActive && !targetTeamId ? ' is-active' : ''}`}
                onClick={handleOpenChat}
              >
                <IconMessage size={16} stroke={1.5} aria-hidden />
                <span className="bch-customers-menu-item-text">
                  <span className="bch-customers-menu-item-title">Chat</span>
                  <span className="bch-customers-menu-item-sub">Conversas com clientes</span>
                </span>
                {hasChatBadge && (
                  <span className="bch-customers-menu-item-badges" aria-hidden="true">
                    {unreadLabel ? (
                      <span className="bch-badge bch-badge--unread">{unreadLabel}</span>
                    ) : null}
                    {inboxLabel ? (
                      <span className="bch-badge bch-badge--inbox">{inboxLabel}</span>
                    ) : null}
                  </span>
                )}
              </button>
              <button
                type="button"
                role="menuitem"
                className={`ec-actions-menu-item${chatActive && targetTeamId ? ' is-active' : ''}`}
                onClick={handleOpenPreAtendimento}
              >
                <IconReportSearch size={16} stroke={1.5} aria-hidden />
                <span className="bch-customers-menu-item-text">
                  <span className="bch-customers-menu-item-title">Pré-atendimento</span>
                  <span className="bch-customers-menu-item-sub">Triagem e atendimentos iniciais</span>
                </span>
                {hasPreBadge && (
                  <span className="bch-customers-menu-item-badges" aria-hidden="true">
                    {preUnreadLabel ? (
                      <span className="bch-badge bch-badge--unread">{preUnreadLabel}</span>
                    ) : null}
                    {preInboxLabel ? (
                      <span className="bch-badge bch-badge--inbox">{preInboxLabel}</span>
                    ) : null}
                    {prePendingOsLabel ? (
                      <span className="bch-badge bch-badge--os">{prePendingOsLabel}</span>
                    ) : null}
                  </span>
                )}
              </button>
              {canAccessCadastro ? (
                <button
                  type="button"
                  role="menuitem"
                  className={`ec-actions-menu-item${cadastroOpen ? ' is-active' : ''}`}
                  onClick={openCadastro}
                >
                  <IconUserQuestion size={16} stroke={1.5} aria-hidden />
                  <span className="bch-customers-menu-item-text">
                    <span className="bch-customers-menu-item-title">Cadastro</span>
                    <span className="bch-customers-menu-item-sub">Usuários pendentes do portal</span>
                  </span>
                  {pendingLabel ? (
                    <span className="bch-customers-menu-item-badges" aria-hidden="true">
                      <span className="bch-badge bch-badge--unread">{pendingLabel}</span>
                    </span>
                  ) : null}
                </button>
              ) : null}
              {showNovosChamados ? (
                <button
                  type="button"
                  role="menuitem"
                  className={`ec-actions-menu-item${novosChamadosActive ? ' is-active' : ''}`}
                  onClick={handleNovosChamados}
                >
                  <Ticket size={16} strokeWidth={1.5} aria-hidden />
                  <span className="bch-customers-menu-item-text">
                    <span className="bch-customers-menu-item-title">Novos chamados</span>
                    <span className="bch-customers-menu-item-sub">{NOVOS_CHAMADOS_SUB}</span>
                  </span>
                  {novosChamadosLabel ? (
                    <span className="bch-customers-menu-item-badges" aria-hidden="true">
                      <span className="bch-badge bch-badge--unread">{novosChamadosLabel}</span>
                    </span>
                  ) : null}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <span className="board-toolbar-separator" aria-hidden="true">
        |
      </span>
      <BoardChatPanel
        open={panelOpen}
        onClose={closePanel}
        session={session}
        onBadgesRefresh={refreshBadges}
        layout={layout}
        onLayoutChange={handleLayoutChange}
        onOpenOrderDetails={onOpenOrderDetails}
        onEncaminharOs={onEncaminharOs}
        layoutLocked={false}
        initialTeamId={targetTeamId}
      />
      {renderCadastroOverlay()}
    </>
  );
}
